import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import { AuthBrandHeader } from "@/components/portal/AuthBrandHeader";
import { ExternalRedirect } from "@/components/portal/ExternalRedirect";
import { HomeTopNav } from "@/components/portal/HomeTopNav";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useVerificationResendCooldown } from "@/hooks/useVerificationResendCooldown";
import {
  isLoginFormValid,
  resolveLoginError,
  toLoginRequest,
  type LoginError,
} from "@/lib/api/login-form";
import { loginUserWithRememberMeFallback } from "@/lib/api/login-user";
import { resendVerification } from "@/lib/api/resend-verification";
import { formatVerificationResendCooldownLabel } from "@/lib/api/verification-resend-cooldown";
import {
  getResendVerificationErrorMessage,
  RESEND_VERIFICATION_SUCCESS_MESSAGE,
} from "@/lib/api/verify-email-form";
import {
  getAuthSession,
  getRememberMePreference,
  setAuthSession,
  setRememberMePreference,
} from "@/lib/auth/session";
import {
  getRequestAccessAuthNotice,
  isExternalPostAuthRedirect,
  navigatePostAuth,
  readAuthReturnPath,
  readAuthReturnPathFromSearch,
  resolveAuthReturnPath,
} from "@/lib/auth/post-auth-redirect";
import { useSandboxCredentialsEnabled } from "@/lib/feature-flags/useSandboxCredentialsEnabled";
import {
  PORTAL_SIGNUP_LEGACY_ROUTE,
  PORTAL_SIGNUP_ROUTE,
} from "@/lib/portal/portal-routes";

export default function LoginPage() {
  usePageTitle("Log in");
  const sandboxCredentialsEnabled = useSandboxCredentialsEnabled();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = resolveAuthReturnPath(
    location.state,
    location.search,
    sandboxCredentialsEnabled ? undefined : "/",
  );
  const requestAccessNotice = getRequestAccessAuthNotice(
    readAuthReturnPath(location.state) ??
      readAuthReturnPathFromSearch(location.search),
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(getRememberMePreference);
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const resendCooldown = useVerificationResendCooldown();
  const [resetSuccessMessage] = useState<string | null>(() => {
    const state = location.state as {
      resetSuccess?: string;
      verifySuccess?: string;
    } | null;
    const message =
      state?.resetSuccess?.trim() || state?.verifySuccess?.trim();
    return message || null;
  });

  useEffect(() => {
    const state = location.state as {
      resetSuccess?: string;
      verifySuccess?: string;
    } | null;
    const message =
      state?.resetSuccess?.trim() || state?.verifySuccess?.trim();
    if (!message) {
      return;
    }

    navigate(location.pathname, {
      replace: true,
      state: { from: returnPath },
    });
  }, [location.pathname, location.state, navigate, returnPath]);

  if (getAuthSession()) {
    if (isExternalPostAuthRedirect(returnPath)) {
      return <ExternalRedirect to={returnPath} />;
    }
    return <Navigate to={returnPath} replace />;
  }

  const canSubmit = isLoginFormValid({ email, password });

  async function handleLogin(event?: { preventDefault(): void }) {
    event?.preventDefault();
    if (!canSubmit) {
      return;
    }

    setLoading(true);
    setError(null);
    setResendNotice(null);
    setResendError(null);
    setRememberMePreference(rememberMe);

    try {
      // AES-273: never send remember_me — the login API rejects unknown fields.
      // AES-303: remember-me only controls client persistence (local vs session).
      const response = await loginUserWithRememberMeFallback(
        toLoginRequest({ email, password }),
      );
      setAuthSession(
        {
          accessToken: response.access_token,
          tokenType: response.token_type,
          expiresIn: response.expires_in,
          user: response.user,
        },
        // Remember me → localStorage (survives browser restart, shared across tabs).
        // Unchecked → sessionStorage (cleared when the browsing session ends).
        { persist: rememberMe ? "local" : "session" },
      );
      navigatePostAuth(navigate, returnPath);
    } catch (err: unknown) {
      setError(resolveLoginError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleResendVerification() {
    const trimmed = email.trim();
    if (!trimmed) {
      setResendError("Enter your email address first.");
      return;
    }
    if (resendCooldown.isCoolingDown) {
      return;
    }

    setResendLoading(true);
    setResendError(null);
    setResendNotice(null);

    try {
      const response = await resendVerification({ email: trimmed });
      setResendNotice(response.message || RESEND_VERIFICATION_SUCCESS_MESSAGE);
      resendCooldown.start();
    } catch (err: unknown) {
      setResendError(getResendVerificationErrorMessage(err));
    } finally {
      setResendLoading(false);
    }
  }

  return (
    <>
      <HomeTopNav />
      <main id="main-content" className="auth-shell">
        <div className="auth-layout">
          <AuthBrandHeader testId="home-login-brand-logo" />

          <div className="auth-card" data-testid="home-login-card">
            <div className="auth-hdr">
              <p className="auth-eye">Welcome back</p>
              <h1 data-testid="home-login-title">Log in to your account</h1>
              <p>Enter your credentials to access the developer portal.</p>
            </div>
            <div className="auth-body">
              {requestAccessNotice ? (
                <div
                  className="notice-msg"
                  data-testid="home-login-request-access-notice"
                  role="status"
                >
                  <i className="ti ti-info-circle" />
                  <span>{requestAccessNotice}</span>
                </div>
              ) : null}

              {resetSuccessMessage && (
                <div
                  className="success-msg"
                  data-testid="home-login-success"
                  role="status"
                >
                  <i className="ti ti-circle-check" />
                  <span>{resetSuccessMessage}</span>
                </div>
              )}

              <form
                className="auth-form"
                data-testid="home-login-form"
                noValidate
                onSubmit={(event) => {
                  void handleLogin(event);
                }}
              >
              {error &&
                (error.waiting ? (
                  <div
                    className="notice-msg"
                    data-testid="home-login-notice"
                    role="status"
                  >
                    <i className="ti ti-clock-hour-4" />
                    <span>{error.message}</span>
                  </div>
                ) : (
                  <div
                    className="error-msg"
                    data-testid="home-login-error"
                    role="alert"
                  >
                    <i className="ti ti-alert-circle" />
                    <span>
                      {error.message}{" "}
                      {error.needsVerification ? (
                        <button
                          type="button"
                          className="auth-link-btn"
                          data-testid="home-login-resend-verification"
                          onClick={handleResendVerification}
                          disabled={resendLoading || resendCooldown.isCoolingDown}
                          style={{ color: "inherit", textDecoration: "underline" }}
                        >
                          {resendLoading
                            ? "Sending…"
                            : formatVerificationResendCooldownLabel(
                                resendCooldown.secondsRemaining,
                              )}
                        </button>
                      ) : null}
                    </span>
                  </div>
                ))}

              {resendNotice ? (
                <div
                  className="success-msg"
                  data-testid="home-login-resend-success"
                  role="status"
                >
                  <i className="ti ti-circle-check" />
                  <span>{resendNotice}</span>
                </div>
              ) : null}

              {resendError ? (
                <div
                  className="error-msg"
                  data-testid="home-login-resend-error"
                  role="alert"
                >
                  <i className="ti ti-alert-circle" />
                  <span>{resendError}</span>
                </div>
              ) : null}

              <div className="field">
                <label htmlFor="home-login-email">Email address</label>
                <input
                  id="home-login-email"
                  type="email"
                  data-testid="home-login-email"
                  placeholder="you@company.com"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(null);
                  }}
                />
              </div>

              <div className="field">
                <label htmlFor="home-login-password">Password</label>
                <div className="pw-input-wrap">
                  <input
                    id="home-login-password"
                    type={showPw ? "text" : "password"}
                    data-testid="home-login-password"
                    placeholder="Your password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError(null);
                    }}
                  />
                  <button
                    className="show-pw-btn"
                    data-testid="home-login-toggle-password"
                    onClick={() => setShowPw((v) => !v)}
                    type="button"
                    aria-label={showPw ? "Hide password" : "Show password"}
                    aria-pressed={showPw}
                  >
                    <i
                      className={`ti ${showPw ? "ti-eye-off" : "ti-eye"}`}
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </div>

              <div className="auth-row">
                <label className="auth-check">
                  <input
                    type="checkbox"
                    data-testid="home-login-remember-me"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  Remember me
                </label>
                <button
                  type="button"
                  className="auth-link-btn"
                  data-testid="home-login-forgot-password"
                  onClick={() => navigate("/forgot-password")}
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                className="submit-btn auth-submit"
                data-testid="home-login-submit"
                disabled={loading || !canSubmit}
              >
                {loading ? (
                  <>
                    <i
                      className="ti ti-loader-2"
                      style={{ animation: "spin 1s linear infinite" }}
                    />{" "}
                    Signing in…
                  </>
                ) : (
                  <>
                    <i className="ti ti-login" /> Log in
                  </>
                )}
              </button>
              </form>
            </div>
          </div>

          <p className="already">
            Don&apos;t have an account?{" "}
            <Link
              to={
                sandboxCredentialsEnabled
                  ? PORTAL_SIGNUP_ROUTE
                  : PORTAL_SIGNUP_LEGACY_ROUTE
              }
              state={{ from: returnPath }}
              data-testid="home-login-signup-link"
            >
              Sign up →
            </Link>
          </p>
        </div>
      </main>
    </>
  );
}
